/**
 * The CRM property the iframe was opened for.
 *
 * Propstack opens the embed with `?objectReferenceId=<their property id>`, and the flow must order for
 * exactly that property. The parameter shares `?lang`'s fate (see `embedLocale`): the entry redirect and
 * every step navigation drop the search string, so it is remembered here on first sight rather than read
 * again later. It is also kept for the tab, because reloading the iframe reloads the step's own URL,
 * which no longer carries the parameter.
 */

const STORAGE_KEY = 'pf-embed-property-ref'

let propertyRef: string | null = null

/** Session storage can be unavailable in a third-party iframe; the in-memory value then stands alone. */
const readStored = (): string | null => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

const writeStored = (value: string | null): void => {
  try {
    if (value) window.sessionStorage.setItem(STORAGE_KEY, value)
    else window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Unavailable storage leaves only the in-memory value, which still covers in-flow navigation.
  }
}

/**
 * Records the reference the first time the URL carries one, and answers with the one in force now.
 *
 * @example
 * rememberEmbedPropertyRef(params.get('objectReferenceId'))  // 'PS-123' on entry, still 'PS-123' on /catalogue
 */
export const rememberEmbedPropertyRef = (fromUrl: string | null): string | null => {
  if (fromUrl) {
    propertyRef = fromUrl
    writeStored(fromUrl)
  }
  return propertyRef ?? readStored()
}

/** Drops the remembered reference. Leaving the embed, and the reset a test needs between cases. */
export const forgetEmbedPropertyRef = (): void => {
  propertyRef = null
  writeStored(null)
}
